import React, { useState } from "react";
import { WidgetCard } from "@/components/WidgetCard";
import { SectionHeader } from "@/components/SectionHeader";
import { getCustomRenderer } from "@/lib/dashboardWidgetRegistry";
import {
  widgetByKey,
  WIDGET_CATEGORIES,
} from "@/lib/dashboardWidgetCatalog";
import {
  sizesFor,
  heightsFor,
  moveToIndex,
} from "@/lib/dashboardLayout";
import {
  ArrowUp,
  ArrowDown,
  X as XIcon,
  GripVertical,
  Maximize2,
} from "lucide-react";

/**
 * DashboardGrid — Phase C Wave 2A.1 reusable grid.
 *
 * Consumed by Dashboard.jsx (per-user) and (in a future wave) by the
 * Admin Role-Default editor. Renders widgets grouped by catalog category
 * in the *user's* saved order. When in edit mode, decorates each cell
 * with size / move / remove controls and enables HTML5 drag/drop.
 *
 * Props:
 *   layout        list[{widget_key, order, size}]
 *   payloads      dict of widget_key → aggregator payload
 *   editMode      bool
 *   onReorder     (nextLayout) => void
 *   onResize      (widget_key, size) => void
 *   onRemove      (widget_key) => void
 *
 * The permission-filtered aggregator remains authoritative — if a user
 * loses a permission, the payload arrives as `unavailable_reason:
 * "permission_denied"` and the underlying renderer shows the Restricted
 * state, even if the widget is still in the saved layout.
 */

const SIZE_TO_COLSPAN = {
  compact:  "col-span-12 sm:col-span-6 lg:col-span-3",
  standard: "col-span-12 sm:col-span-6 lg:col-span-6",
  wide:     "col-span-12",
};

const Cell = ({ item, payload, editMode, onResize, onHeight, onRemove, onMoveUp, onMoveDown,
                onDragStart, onDragOver, onDrop, isFirst, isLast }) => {
  const meta = widgetByKey(item.widget_key);
  const size = item.size || "standard";
  const supported = sizesFor(item.widget_key);
  const supportedH = heightsFor(item.widget_key);
  const height = item.height || (supportedH.length > 0 ? supportedH[0] : null);
  const Custom = getCustomRenderer(item.widget_key);

  if (!meta) return null;

  // Fleet Map UX Hardening — heightPreset is forwarded to renderers that
  // opt in via WIDGET_HEIGHT_OPTIONS. Non-opt-in widgets ignore it.
  const body = Custom ? (
    <Custom payload={payload} heightPreset={height} />
  ) : (
    <WidgetCard
      widgetKey={item.widget_key}
      label={meta.label}
      category={meta.category}
      payload={payload}
      isFuturePlaceholder={meta.is_future_placeholder === true}
    />
  );

  return (
    <div
      className={`${SIZE_TO_COLSPAN[size]} relative`}
      data-testid={`grid-cell-${item.widget_key}`}
      data-widget-size={size}
      data-widget-height={height || undefined}
      draggable={editMode}
      onDragStart={(e) => onDragStart?.(e, item.widget_key)}
      onDragOver={(e) => { if (editMode) e.preventDefault(); }}
      onDrop={(e) => onDrop?.(e, item.widget_key)}
    >
      {editMode && (
        <div
          data-testid={`grid-cell-controls-${item.widget_key}`}
          className="absolute -top-2 right-2 z-10 flex items-center gap-1 bg-white dark:bg-graphite border border-coach-silver rounded-sm shadow-sm px-1 py-0.5"
        >
          <button
            type="button"
            title="Move up"
            data-testid={`grid-cell-up-${item.widget_key}`}
            disabled={isFirst}
            onClick={onMoveUp}
            className="p-1 text-steel hover:text-coach-blue disabled:opacity-30"
          >
            <ArrowUp size={12} />
          </button>
          <button
            type="button"
            title="Move down"
            data-testid={`grid-cell-down-${item.widget_key}`}
            disabled={isLast}
            onClick={onMoveDown}
            className="p-1 text-steel hover:text-coach-blue disabled:opacity-30"
          >
            <ArrowDown size={12} />
          </button>
          {supported.length > 1 && (
            <select
              data-testid={`grid-cell-size-${item.widget_key}`}
              value={size}
              onChange={(e) => onResize?.(item.widget_key, e.target.value)}
              className="text-[10.5px] border-l border-coach-silver pl-1 pr-0.5 py-0.5 bg-transparent focus:outline-none"
              title="Width"
            >
              {supported.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {supportedH.length > 1 && (
            <select
              data-testid={`grid-cell-height-${item.widget_key}`}
              value={height || supportedH[0]}
              onChange={(e) => onHeight?.(item.widget_key, e.target.value)}
              className="text-[10.5px] border-l border-coach-silver pl-1 pr-0.5 py-0.5 bg-transparent focus:outline-none"
              title="Height"
            >
              {supportedH.map((h) => (
                <option key={h} value={h}>{h.replace("_", " ")}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            title="Remove"
            data-testid={`grid-cell-remove-${item.widget_key}`}
            onClick={onRemove}
            className="p-1 text-steel hover:text-critical-red"
          >
            <XIcon size={12} />
          </button>
          <span
            className="pl-1 pr-0.5 text-steel cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <GripVertical size={12} />
          </span>
        </div>
      )}
      {body}
    </div>
  );
};

export default function DashboardGrid({
  layout,
  payloads,
  editMode = false,
  onReorder,
  onResize,
  onHeight,
  onRemove,
}) {
  const [dragKey, setDragKey] = useState(null);

  const handleDragStart = (_e, key) => setDragKey(key);
  const handleDrop = (_e, targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    const targetIdx = layout.findIndex((it) => it.widget_key === targetKey);
    if (targetIdx < 0) return;
    onReorder?.(moveToIndex(layout, dragKey, targetIdx));
    setDragKey(null);
  };

  // Group in the same category buckets used in Wave 1 for visual
  // consistency. Within a category we honour the user's order.
  const grouped = React.useMemo(() => {
    const byCat = new Map();
    for (const it of layout) {
      const meta = widgetByKey(it.widget_key);
      if (!meta) continue;
      if (!byCat.has(meta.category)) byCat.set(meta.category, []);
      byCat.get(meta.category).push(it);
    }
    return WIDGET_CATEGORIES
      .filter((c) => byCat.has(c))
      .map((c) => ({ category: c, items: byCat.get(c) }));
  }, [layout]);

  return (
    <div className="space-y-8" data-testid="dashboard-widget-groups">
      {grouped.map((group) => (
        <section
          key={group.category}
          data-testid={`widget-group-${group.category.toLowerCase().replaceAll(" ", "-")}`}
        >
          <SectionHeader
            testid={`dashboard-section-header-${group.category.toLowerCase().replaceAll(" ", "-")}`}
            right={<span className="text-[10.5px] text-steel">{group.items.length}</span>}
          >
            {group.category}
          </SectionHeader>
          <div className="grid grid-cols-12 gap-3">
            {group.items.map((item, idxInGroup) => {
              const globalIdx = layout.findIndex(
                (it) => it.widget_key === item.widget_key
              );
              // Phase C Wave 2A.1 hotfix: Up/Down must reorder WITHIN the
              // visible category, not against the flat layout. Otherwise a
              // click on "last in Operations" swaps against the first item
              // of the next category and re-grouping makes the shuffle
              // invisible to the user.
              const isFirstInGroup = idxInGroup === 0;
              const isLastInGroup = idxInGroup === group.items.length - 1;
              const prevInGroup = !isFirstInGroup ? group.items[idxInGroup - 1] : null;
              const nextInGroup = !isLastInGroup ? group.items[idxInGroup + 1] : null;
              const targetIdxUp   = prevInGroup ? layout.findIndex((it) => it.widget_key === prevInGroup.widget_key) : globalIdx;
              const targetIdxDown = nextInGroup ? layout.findIndex((it) => it.widget_key === nextInGroup.widget_key) : globalIdx;
              return (
                <Cell
                  key={item.widget_key}
                  item={item}
                  payload={payloads[item.widget_key]}
                  editMode={editMode}
                  isFirst={isFirstInGroup}
                  isLast={isLastInGroup}
                  onMoveUp={() => onReorder?.(moveToIndex(layout, item.widget_key, targetIdxUp))}
                  onMoveDown={() => onReorder?.(moveToIndex(layout, item.widget_key, targetIdxDown))}
                  onResize={onResize}
                  onHeight={onHeight}
                  onRemove={() => onRemove?.(item.widget_key)}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

// Icon re-export so other components can import from one place.
export { Maximize2 };
